import { z } from 'zod'

import { PaginationParamsSchema, PaginationSchema } from '@/schemas/pagination'

enum Status {
  Ativo = 'Ativo',
  Inativo = 'Inativo',
}

enum FilterStatus {
  Ativo = '',
  Inativo = 'inativo',
  Todos = 'todos',
}

enum PriceType {
  Vazio = 0,
  Unitário = 1,
  Variável = 2,
  'Variável múltiplo' = 3,
}

enum TicketComboType {
  Vazio = 0,
  'Ticket Unitário' = 1,
  'Ticket Simples' = 2,
  'Ticket Simples Detalhado' = 3,
}

const ProductBaseSchema = z.object({
  cod_produto: z.number(),
  nome: z.string(),
  produto_pai: z
    .object({
      cod_produto: z.number(),
      nome: z.string(),
      situacao: z.enum([Status.Ativo, Status.Inativo]),
      posicao: z.number().nullable(),
    })
    .nullable(),
  imagem: z.string().nullable(),
  imagem_path: z.string().nullable(),
  tipo: z.number(),
  vlr_custo: z.number().nullable(),
  tipo_valor: z.nativeEnum(PriceType),
  consumo: z.boolean(),
  permitir_observacao: z.boolean().nullable(),
  servico: z.boolean(),
  cod_externo: z.string().nullable(),
  unidade_medida: z.string().nullable(),
  situacao: z.enum([Status.Ativo, Status.Inativo]),
})

type ProductBase = z.infer<typeof ProductBaseSchema>

const ProductTaxSchema = z.object({
  emitir_nfce: z.boolean(),
  ncm: z.string().nullable(),
  cfop: z.string().nullable(),
  icms_origem: z.string().nullable(),
  icms_situacao_tributaria: z.string().nullable(),
  icms_aliquota: z.number().nullable(),
  icms_percentual_reducao_base_calculo: z.number().nullable(),
  icms_modalidade_base_calculo: z.string().nullable(),
  pis_tipo_tributacao: z.string().nullable(),
  pis_aliquota_percentual: z.number().nullable(),
  cofins_tipo_tributacao: z.string().nullable(),
  cofins_aliquota_percentual: z.number().nullable(),
  cest: z.string().nullable(),
})

type ProductTax = z.infer<typeof ProductTaxSchema>

const ProductSchema = z
  .object({
    tipo_ticket_combo: z.nativeEnum(TicketComboType).nullable(),
    produto_combo_list: z
      .array(
        z.object({
          cod_produto_combo: z.number(),
          produto: z
            .object({})
            .merge(ProductBaseSchema)
            .merge(ProductTaxSchema),
          qtde_produto: z.number(),
          vlr_produto: z.number(),
        }),
      )
      .optional(),
  })
  .merge(ProductBaseSchema)
  .merge(ProductTaxSchema)

type Product = z.infer<typeof ProductSchema>

const ProductsSchema = z.array(ProductSchema)

type Products = z.infer<typeof ProductsSchema>

const UpdateProductBodySchema = z.object({
  cod_produto: z.number(),
  nome: z.string(),
  produto_pai: z.number().nullable(),
  imagem: z.string().nullable(),
  situacao: z.enum([Status.Ativo, Status.Inativo]),
  tipo: z.number(),
  consumo: z.boolean(),
  permitir_observacao: z.boolean(),
  servico: z.boolean(),
  vlr_custo: z.number(),
  tipo_valor: z.number(),
  unidade_medida: z.string().nullable(),
  cod_externo: z.string().nullable(),
  cfop: z.string().nullable(),
  ncm: z.string().nullable(),
  icms_situacao_tributaria: z.string().nullable(),
  icms_origem: z.string().nullable(),
  icms_aliquota: z.number().nullable(),
  icms_percentual_reducao_base_calculo: z.number().nullable(),
  icms_modalidade_base_calculo: z.string().nullable(),
  pis_situacao_tributaria: z.string().nullable(),
  pis_tipo_tributacao: z.string().nullable(),
  pis_aliquota_percentual: z.number().nullable(),
  cofins_situacao_tributaria: z.string().nullable(),
  cofins_tipo_tributacao: z.string().nullable(),
  cofins_aliquota_percentual: z.number().nullable(),
  cest: z.string().nullable(),
  grupo_tributario: z.string().nullable(),
  tipo_ticket_combo: z.number().nullable(),
  produto_combo_list: z
    .array(
      z.object({
        cod_produto_combo: z.number().optional(),
        produto: z.number().optional(),
        produto_nome: z.string().optional(),
        qtde_produto: z.number(),
        vlr_produto: z.number(),
      }),
    )
    .nullable(),
})

type UpdateProductBody = z.infer<typeof UpdateProductBodySchema>

const UpdateProductParamsSchema = z.object({
  id: z.number(),
})

type UpdateProductParams = z.infer<typeof UpdateProductParamsSchema>

const UpdateProductPositionBodySchema = z.object({
  posicao: z.number(),
})

type UpdateProductPositionBody = z.infer<typeof UpdateProductPositionBodySchema>

const TaxGroupSchema = z.array(
  z.object({
    cod_grupo_tributario: z.number(),
    descricao: z.string(),
    ncm: z.string().nullable(),
    icms_situacao_tributaria: z.string().nullable(),
    icms_aliquota: z.number().nullable(),
    icms_origem: z.string().nullable(),
    cfop: z.string().nullable(),
    situacao: z.enum([Status.Ativo, Status.Inativo]),
    cod_empresa: z.number(),
    pis_situacao_tributaria: z.string().nullable(),
    cofins_situacao_tributaria: z.string().nullable(),
    icms_modalidade_base_calculo: z.string().nullable(),
    pis_tipo_tributacao: z.string().nullable(),
    pis_aliquota_percentual: z.number().nullable(),
    cofins_tipo_tributacao: z.string().nullable(),
    cofins_aliquota_percentual: z.number().nullable(),
    icms_percentual_reducao_base_calculo: z.string().nullable(),
    cest: z.string().nullable(),
  }),
)

type TaxGroup = z.infer<typeof TaxGroupSchema>

const UpdateProductResponseSchema = z
  .object({
    grupo_tributario: TaxGroupSchema.element.nullable(),
    pis_situacao_tributaria: z.string().nullable(),
    cofins_situacao_tributaria: z.string().nullable(),
  })
  .merge(ProductSchema)

type UpdateProductResponse = z.infer<typeof UpdateProductResponseSchema>

const TaxListParamsSchema = PaginationParamsSchema.pick({ search: true })

type TaxListParams = z.infer<typeof TaxListParamsSchema>

const TaxListSchema = z.array(
  z.object({
    id: z.string(),
    text: z.string(),
  }),
)

type TaxList = z.infer<typeof TaxListSchema>

const CreatePriceListBodySchema = z.object({
  nome: z.string().min(1, 'Nome obrigatório'),
  situacao: z.enum([Status.Ativo, Status.Inativo]).optional(),
})

type CreatePriceListBody = z.infer<typeof CreatePriceListBodySchema>

const CreatePriceListResponseSchema = z.object({
  cod_lista_preco: z.number(),
  nome: z.string(),
  situacao: z.enum([Status.Ativo, Status.Inativo]),
  qtde_produto: z.number().nullable(),
})

type CreatePriceListResponse = z.infer<typeof CreatePriceListResponseSchema>

const DeletePriceListParamsSchema = z.object({
  id: z.number(),
})

type DeletePriceListParams = z.infer<typeof DeletePriceListParamsSchema>

const UpdatePriceListBodySchema = z.object({
  nome: z.string().min(1, 'Nome obrigatório'),
  situacao: z.enum([Status.Ativo, Status.Inativo]),
})

type UpdatePriceListBody = z.infer<typeof UpdatePriceListBodySchema>

const UpdatePriceListParamsSchema = z.object({
  id: z.number(),
})

type UpdatePriceListParams = z.infer<typeof UpdatePriceListParamsSchema>

const PriceListParamsSchema = z
  .object({
    orderField: z.string().min(1).optional(),
    orderDirection: z.string().min(1).optional(),
    status: z
      .enum([FilterStatus.Ativo, FilterStatus.Inativo, FilterStatus.Todos])
      .optional(),
  })
  .merge(PaginationParamsSchema)

type PriceListParams = z.infer<typeof PriceListParamsSchema>

const PriceListResponseSchema = z
  .object({
    data: z.array(CreatePriceListResponseSchema),
  })
  .merge(PaginationSchema)

type PriceListResponse = z.infer<typeof PriceListResponseSchema>

const PriceListProductsParamsSchema = z.object({
  id: z.number(),
})

type PriceListProductsParams = z.infer<typeof PriceListProductsParamsSchema>

const PriceListProductsResponseSchema = z.array(
  z.object({
    cod_produto_lista_preco: z.number(),
    produto: z.object({}).merge(ProductSchema),
    situacao: z.number(),
    posicao: z.number(),
    vlr_produto: z.number().or(z.string()),
    limite_venda: z.number(),
    qtde_vendido: z.number().nullable(),
  }),
)

type PriceListProductsResponse = z.infer<typeof PriceListProductsResponseSchema>

const CreatePriceListProductBodySchema = z.object({
  produto: z.string(),
  categoria: z.string(),
  valor: z.string(),
  imagem: z.string().optional(),
})

type CreatePriceListProductBody = z.infer<
  typeof CreatePriceListProductBodySchema
>

const CreatePriceListProductParamsSchema = z.object({
  id: z.number(),
})

type CreatePriceListProductParams = z.infer<
  typeof CreatePriceListProductParamsSchema
>

const UpdatePriceListProductBodySchema = z.object({
  produto: z.string(),
  categoria: z.string().optional(),
  valor: z.string(),
  imagem: z.string().optional(),
})

type UpdatePriceListProductBody = z.infer<
  typeof UpdatePriceListProductBodySchema
>

const UpdatePriceListProductParamsSchema = z.object({
  id: z.number(),
})

type UpdatePriceListProductParams = z.infer<
  typeof UpdatePriceListProductParamsSchema
>

const CreatePriceListProductResponseSchema = z
  .object({})
  .merge(PriceListProductsResponseSchema.element)

type CreatePriceListProductResponse = z.infer<
  typeof CreatePriceListProductResponseSchema
>

const DeletePriceListProductParamsSchema = z.object({
  id: z.number(),
})

type DeletePriceListProductParams = z.infer<
  typeof DeletePriceListProductParamsSchema
>

const PriceListProductOptionsParamsSchema = z.object({
  id: z.number(),
})

type PriceListProductOptionsParams = z.infer<
  typeof PriceListProductOptionsParamsSchema
>

const PriceListProductOptionsResponseSchema = z.array(ProductSchema)

type PriceListProductOptionsResponse = z.infer<
  typeof PriceListProductOptionsResponseSchema
>

const CategoryOptionsResponseSchema = z.array(
  z.object({
    cod_produto: z.number(),
    nome: z.string(),
    situacao: z.enum([Status.Ativo, Status.Inativo]),
    posicao: z.number().nullable(),
  }),
)

type CategoryOptionsResponse = z.infer<typeof CategoryOptionsResponseSchema>

const ImageSchema = z
  .object({
    descricao: z.string(),
    imagem: z.string(),
    imagem_path: z.string(),
  })
  .nullable()

type Image = z.infer<typeof ImageSchema>

const PriceListImageGalleryParamsSchema = z
  .object({})
  .merge(PaginationParamsSchema)

type PriceListImageGalleryParams = z.infer<
  typeof PriceListImageGalleryParamsSchema
>

const PriceListImageGalleryResponseSchema = z
  .object({
    data: z.array(ImageSchema.unwrap()),
  })
  .merge(PaginationSchema)

type PriceListImageGalleryResponse = z.infer<
  typeof PriceListImageGalleryResponseSchema
>

const UploadImageBodySchema = z.object({
  file: z.string(),
})

type UploadImageBody = z.infer<typeof UploadImageBodySchema>

const UploadImageParamsSchema = z.object({
  id: z.number(),
})

type UploadImageParams = z.infer<typeof UploadImageParamsSchema>

const UploadImageResponseSchema = z.object({}).merge(ProductBaseSchema)

type UploadImageResponse = z.infer<typeof UploadImageResponseSchema>

export {
  Status,
  FilterStatus,
  PriceType,
  TicketComboType,
  ProductBaseSchema,
  ProductTaxSchema,
  ProductSchema,
  ProductsSchema,
  UpdateProductBodySchema,
  UpdateProductParamsSchema,
  UpdateProductResponseSchema,
  UpdateProductPositionBodySchema,
  TaxListParamsSchema,
  TaxListSchema,
  TaxGroupSchema,
  CreatePriceListBodySchema,
  CreatePriceListResponseSchema,
  DeletePriceListParamsSchema,
  UpdatePriceListBodySchema,
  UpdatePriceListParamsSchema,
  PriceListParamsSchema,
  PriceListResponseSchema,
  PriceListProductsParamsSchema,
  PriceListProductsResponseSchema,
  CreatePriceListProductBodySchema,
  CreatePriceListProductParamsSchema,
  CreatePriceListProductResponseSchema,
  DeletePriceListProductParamsSchema,
  UpdatePriceListProductBodySchema,
  UpdatePriceListProductParamsSchema,
  PriceListProductOptionsParamsSchema,
  PriceListProductOptionsResponseSchema,
  CategoryOptionsResponseSchema,
  ImageSchema,
  PriceListImageGalleryParamsSchema,
  PriceListImageGalleryResponseSchema,
  UploadImageBodySchema,
  UploadImageParamsSchema,
  UploadImageResponseSchema,
  type ProductBase,
  type ProductTax,
  type Product,
  type Products,
  type UpdateProductBody,
  type UpdateProductParams,
  type UpdateProductResponse,
  type UpdateProductPositionBody,
  type TaxListParams,
  type TaxList,
  type TaxGroup,
  type CreatePriceListBody,
  type DeletePriceListParams,
  type UpdatePriceListBody,
  type UpdatePriceListParams,
  type CreatePriceListResponse,
  type PriceListParams,
  type PriceListResponse,
  type PriceListProductsParams,
  type PriceListProductsResponse,
  type CreatePriceListProductBody,
  type CreatePriceListProductParams,
  type CreatePriceListProductResponse,
  type DeletePriceListProductParams,
  type UpdatePriceListProductBody,
  type UpdatePriceListProductParams,
  type PriceListProductOptionsParams,
  type PriceListProductOptionsResponse,
  type CategoryOptionsResponse,
  type Image,
  type PriceListImageGalleryParams,
  type PriceListImageGalleryResponse,
  type UploadImageBody,
  type UploadImageParams,
  type UploadImageResponse,
}
